// ✅ FIX: SignInModal.jsx to load and persist profilePic correctly
import React, { useState, useContext, useRef } from 'react';
import { UserContext } from '../UserContext';
import { useTranslation } from 'react-i18next';
import '../styles/SignInModal.css';

const SIGNIN_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:4567/api/signin'
    : 'https://health-ai-zfr7.onrender.com/api/signin';

function SignInModal({ onClose, onSigninSuccess }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { updateProfilePic } = useContext(UserContext);
  const abortRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  async function handleSubmit(e) {
    e?.preventDefault();
    if (loading) return;
    setErrorMessage('');

    if (!form.email || !form.password) {
      setErrorMessage('Please enter email and password.');
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const timeout = setTimeout(() => controller.abort('timeout'), 12000);

    try {
      setLoading(true);

      const resp = await fetch(SIGNIN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
        signal: controller.signal,
      });

      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !(result.ok || result.status === 'success')) {
        setErrorMessage(result?.message || `Sign in failed (${resp.status})`);
        return;
      }

      const u = result.user || {};
      if (result.token) localStorage.setItem('token', result.token);

      const payload = {
        isLoggedIn: 'true',
        userName: u.name || '',
        userEmail: u.email || form.email,
        userPic: u.profilePic || '',
        userDOB: u.dob || '',
        userHeight: u.height || '',
        userWeight: u.weight || '',
        userGender: u.gender || '',
        zodiac: u.horoscope || '',
      };
      Object.entries(payload).forEach(([k, v]) => localStorage.setItem(k, v));

      const h = parseFloat(u?.height);
      const w = parseFloat(u?.weight);
      if (h > 0 && w > 0) {
        const bmi = w / Math.pow(h / 100, 2);
        localStorage.setItem('lastBMI', String(bmi.toFixed(1)));
        sessionStorage.setItem('lastBMI', String(bmi.toFixed(1)));
      }

      // ✅ Ensure context is updated with base64 image or URL
      if (u?.profilePic) {
        updateProfilePic(u.profilePic);
      }

      window.dispatchEvent(new Event('auth-change'));
      window.dispatchEvent(new Event('profile-change'));

      onSigninSuccess?.();
      onClose?.();
    } catch (err) {
      setErrorMessage(err?.name === 'AbortError' ? 'Sign in timed out.' : 'Could not reach the server.');
    } finally {
      clearTimeout(timeout);
      setLoading(false);
      abortRef.current = null;
    }
  }

  return (
    <div className="signin-overlay" role="dialog" aria-modal="true">
      <div className="signin-popup">
        <div className="signin-header">
          <h2>{t('sign_in')}</h2>
          <span className="signin-key" aria-hidden>🔑</span>
        </div>

        <form onSubmit={handleSubmit} className="signin-form">
          <input
            name="email"
            type="email"
            className="signin-input"
            placeholder={t('email')}
            value={form.email}
            onChange={handleChange}
            disabled={loading}
            required
          />

          <input
            name="password"
            type="password"
            className="signin-input"
            placeholder={t('password')}
            value={form.password}
            onChange={handleChange}
            disabled={loading}
            required
          />

          {errorMessage && <p className="signin-error">{errorMessage}</p>}

          <div className="signin-actions">
            <button type="submit" className="signin-primary" disabled={loading}>
              {loading ? t('signing_in') : t('sign_in')}
            </button>
            <button type="button" className="signin-secondary" onClick={onClose} disabled={loading}>
              {t('close')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SignInModal;